import { dedent } from 'ts-dedent';
import { ColumnDataType, Language, Provider, WorkflowComponentParamType, WorkflowParameterValueErrorType } from '../types';
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent';
class Join extends SingleOutputWorkflowComponent {
    constructor() {
        super({
            name: 'native.join',
            group: 'Joins',
            title: 'Join',
            description: `
      This component combines rows from two separate tables based on a related column between them.
      `,
            starred: true
        }, [
            {
                name: 'maintable',
                type: WorkflowComponentParamType.InputTable,
                title: 'Main table'
            },
            {
                name: 'secondarytable',
                type: WorkflowComponentParamType.InputTable,
                title: 'Secondary table'
            },
            {
                name: 'maincolumn',
                type: WorkflowComponentParamType.Column,
                title: 'Column in main table',
                parent: 'maintable',
                dataType: [
                    ColumnDataType.Boolean,
                    ColumnDataType.Date,
                    ColumnDataType.Datetime,
                    ColumnDataType.Time,
                    ColumnDataType.Timestamp,
                    ColumnDataType.Number,
                    ColumnDataType.String
                ]
            },
            {
                name: 'secondarycolumn',
                type: WorkflowComponentParamType.Column,
                title: 'Column in secondary table',
                parent: 'secondarytable',
                dataType: [
                    ColumnDataType.Boolean,
                    ColumnDataType.Date,
                    ColumnDataType.Datetime,
                    ColumnDataType.Time,
                    ColumnDataType.Timestamp,
                    ColumnDataType.Number,
                    ColumnDataType.String
                ]
            },
            {
                name: 'jointype',
                type: WorkflowComponentParamType.Selection,
                title: 'Join type',
                options: ['Inner', 'Left', 'Right', 'Full outer'],
                default: 'Inner'
            }
        ]);
    }
    async getCode({ workflow, params, connection, codeType, schemas }) {
        let outputTablename = connection.getOutputTableName(workflow.id, this.metadata.name, params, 'result', codeType);
        const prepParams = this._prepareParameters(params);
        let colString = '';
        const columns = schemas?.input?.['secondarytable'];
        if (columns) {
            colString = columns
                .map((x) => `_joined.${x.name} AS ${x.name}_joined`)
                .join(', ');
        }
        else {
            return {
                query: '',
                outputs: {}
            };
        }
        const select = dedent `
        SELECT ${connection.type === Provider.DatabricksRest
            ? '/*+ REPARTITION(40) */'
            : ''}
          _main.*,
          ${colString}
        FROM
          ${connection.quoteName(prepParams.maintable)} AS _main
        ${prepParams.jointype.toUpperCase()} JOIN
          ${connection.quoteName(prepParams.secondarytable)} AS _joined
        ON
          _main.${prepParams.maincolumn} = _joined.${prepParams.secondarycolumn}
      `;
        let createTable = connection.getCreateTempTableQuery(outputTablename, select, codeType, null);
        if (connection.type === Provider.DatabricksRest) {
            createTable = connection.wrapSQLCodeAsPythonCall(createTable);
        }
        return {
            query: createTable,
            outputs: { result: outputTablename }
        };
    }
    checkInputValues({ params, resolvedParamValues, connection, dryRun, schemas, variables }) {
        const problems = super.checkInputValues({
            params,
            resolvedParamValues,
            connection,
            dryRun,
            schemas,
            variables
        });
        if (schemas && params.maintable && params.secondarytable) {
            let firstType, secondType;
            const mainTable = params.maintable;
            const secondaryTable = params.secondarytable;
            if (mainTable.value && secondaryTable.value) {
                const { component: mainComponent, output: mainOutput } = mainTable.value;
                const mainComponentSchemas = schemas.filter((x) => x.componentName === mainComponent)?.[0];
                if (mainComponentSchemas) {
                    const tableSchema = mainComponentSchemas.schemas.filter((x) => x.name === mainOutput)?.[0];
                    if (tableSchema && tableSchema.schema) {
                        firstType = tableSchema.schema.find((x) => x.name === resolvedParamValues.maincolumn)?.type;
                    }
                }
                const { component: secondaryComponent, output: secondaryOutput } = secondaryTable.value;
                const secondaryComponentSchemas = schemas.filter((x) => x.componentName === secondaryComponent)?.[0];
                if (secondaryComponentSchemas) {
                    const tableSchema = secondaryComponentSchemas.schemas.filter((x) => x.name === secondaryOutput)?.[0];
                    if (tableSchema && tableSchema.schema) {
                        secondType = tableSchema.schema.find((x) => x.name === resolvedParamValues.secondarycolumn)?.type;
                    }
                }
                if (firstType && secondType && firstType !== secondType) {
                    problems.push({
                        parameter: 'tables',
                        type: WorkflowParameterValueErrorType.WrongValue,
                        message: 'Type of selected columns must be identical'
                    });
                }
            }
        }
        return problems;
    }
    language(provider) {
        if (provider === Provider.DatabricksRest) {
            return Language.Python;
        }
        return Language.SQL;
    }
    getVersion(provider) {
        return '1.2';
    }
    canProvideOutputSchemas() {
        return true;
    }
    getOutputSchema({ columns }) {
        const schema = columns.maintable.map((column) => {
            return {
                name: `${column.name}`,
                type: column.type
            };
        });
        columns.secondarytable.forEach((column) => {
            schema.push({
                name: `${column.name}_joined`,
                type: column.type
            });
        });
        return schema;
    }
}
export { Join };
